import { Segment } from '@models/Segment'
import { createSelector, createSlice } from '@reduxjs/toolkit'
import { initialQuery, TableParams } from '@supplement-bacon/sb-table-filters'

import {
  baseReducers,
  getIdOrModelId,
  SBAPI,
  SBSelectRaw,
  SBState,
} from '@supplement-bacon/react-toolkit/utils'
import { segmentSchema } from '../models/schema'
import {
  createSegment as createSegmentAPI,
  deleteSegment as deleteSegmentAPI,
  fetchSegmentById,
  fetchSegments,
  updateSegment as updateSegmentAPI,
} from '../services/SegmentService'
import { AppDispatch, RootState } from './store'

const initialState: SBState<Segment> = {
  isLoading: false,
  error: null,
  items: {},
  ids: [],
  selectedId: undefined,
  query: initialQuery,
}

const slice = createSlice({
  name: 'segment',
  initialState,
  reducers: baseReducers,
})

// Reducer
export default slice.reducer
export const {
  getItemsSuccess: getSegmentsSuccess,
  setQuery: setSegmentQuery,
  reset: resetSegmentState,
  resetQueryAndIds: resetSegmentQueryAndIds,
} = slice.actions

/**
 * Selectors
 */

const selectRawItems: SBSelectRaw<RootState, { [key: number]: Segment }> = (
  state: RootState
) => state[slice.name].items
const selectRawIds: SBSelectRaw<RootState, number[]> = (state: RootState) =>
  state[slice.name].ids
const selectRawSelectedId: SBSelectRaw<RootState, number | undefined> = (
  state: RootState
) => state[slice.name].selectedId

export const selectSegments = () =>
  createSelector(
    [selectRawItems, selectRawIds],
    (items, ids) => ids.map((id) => items[id]).filter((i) => i)
    // Filter allow to return only non-null elements
  )
export const selectSelectedSegment = () =>
  createSelector([selectRawItems, selectRawSelectedId], (items, id) =>
    id !== undefined ? items[id] : undefined
  )
export const selectSegmentById = (id: number) =>
  createSelector([selectRawItems], (items) =>
    items.hasOwnProperty(id) ? items[id] : undefined
  )
export const selectSegmentsByIds = (ids: number[]) =>
  createSelector([selectRawItems], (items) =>
    ids.filter((id) => items.hasOwnProperty(id)).map((id) => items[id])
  )

/**
 * Actions
 */

export const setSelectedSegment =
  (segment: Segment | number) => async (dispatch: AppDispatch) =>
    dispatch(slice.actions.setSelectedId(getIdOrModelId<Segment>(segment)))

export const getSegments = (params: TableParams) =>
  SBAPI.FetchPaginatedDispatched(
    fetchSegments(params),
    [segmentSchema],
    slice.actions
  )

/**
 * Custom create thunk (not `SBAPI.CreateDispatched`).
 *
 * `CreateDispatched` normalizes `response.data.data`, i.e. it expects a
 * double-nested body. `POST /marketing/audiences` returns the created resource
 * single-nested under `data` (`{ status, data: <audience> }`, a tested bo-api
 * contract), so `CreateDispatched` crashes normalizr and surfaces a false
 * error. We consume the real shape here: resolve on success (the drawer closes;
 * the Segments page refetches audiences on mount), reject on failure so the
 * form stays open with the error. Note `SBAPI.Post` resolves with
 * `{ message, error }` on HTTP failure instead of rejecting.
 */
export const createSegment =
  (segment: Segment) => async (dispatch: AppDispatch) => {
    dispatch(slice.actions.startLoading())
    try {
      const res: any = await createSegmentAPI(segment)
      if (res && res.error) throw res
      return res?.data as Segment
    } catch (error: any) {
      dispatch(slice.actions.hasError(String(error?.message ?? 'error')))
      throw error
    } finally {
      dispatch(slice.actions.stopLoading())
    }
  }

export const getSegmentWithId = (id: number) =>
  SBAPI.FetchDispatched<Segment>(
    fetchSegmentById(id),
    segmentSchema,
    slice.actions
  )

export const updateSegment = (segment: Segment) =>
  SBAPI.UpdateDispatched<Segment>(
    updateSegmentAPI(segment),
    segment,
    slice.actions
  )

export const deleteSegment = (segment: Segment) =>
  SBAPI.DestroyDispatched<Segment>(
    deleteSegmentAPI(segment),
    segment,
    slice.actions
  )
